import { cn } from '@neo/lib/utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@neo/primitives/dialog'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { groupBy } from 'lodash'
import { useLayoutEffect, useRef } from 'react'
import { useFlowStore } from '../../../store'
import type { EvalTestCase } from '../types'

interface ConversationTimelineModalProps {
  isOpen: boolean
  onClose: () => void
  testCase: EvalTestCase
}

interface IntentDetectedCardProps {
  intentName: string
  previousNode?: string | null
  newNode?: string | null
}

const IntentDetectedCard = ({ intentName, previousNode, newNode }: IntentDetectedCardProps) => {
  return (
    <div className="rounded-xl border border-st2 bg-bubble-agent px-4 py-3 w-fit">
      <div className="mb-2 text-xs-600">Intent Detected:</div>
      <div className="space-y-2">
        <div className="text-xs-400 grid grid-cols-8">
          <span className="col-span-3">Name</span>
          <span className="text-left col-span-5">: {intentName}</span>
        </div>
        {previousNode && (
          <div className="text-xs-400 text-tertiary grid grid-cols-8">
            <span className="col-span-3">Previous Node</span>
            <span className="text-left col-span-5">: {previousNode}</span>
          </div>
        )}
        {newNode && (
          <div className="text-xs-400 text-tertiary grid grid-cols-8">
            <span className="col-span-3">New Node</span>
            <span className="text-left col-span-5">: {newNode}</span>
          </div>
        )}
      </div>
    </div>
  )
}

interface VariablesExtractedCardProps {
  variables: Record<string, unknown>
}

const VariablesExtractedCard = ({ variables }: VariablesExtractedCardProps) => {
  const formatVariableValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return ''
    }

    if (typeof value === 'object' && value !== null && 'variable_value' in value) {
      const variableObj = value as { variable_value: unknown }
      return formatVariableValue(variableObj.variable_value)
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value)
    }

    return JSON.stringify(value)
  }

  return (
    <div className="rounded-xl border border-st2 bg-bubble-agent px-4 py-3 w-fit">
      <div className="mb-2 text-xs-600">Variables Extracted</div>
      <div className="space-y-2">
        {Object.entries(variables).map(([key, value]) => (
          <div key={key} className="text-xs-400 grid grid-cols-12 col-span-12 gap-4">
            <span className="col-span-7">{key}</span>
            <span className="text-left col-span-5">: {formatVariableValue(value)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ToolCallIndicatorProps {
  toolName: string
}

const ToolCallIndicator = ({ toolName }: ToolCallIndicatorProps) => {
  return (
    <div className="flex w-full justify-center">
      <span className="text-p-500">{toolName} tool call was made</span>
    </div>
  )
}

export const ConversationTimelineModal = ({
  isOpen,
  onClose,
  testCase,
}: ConversationTimelineModalProps) => {
  const flowInstance = useFlowStore((state) => state.flowInstance)
  const conversationScrollRef = useRef<HTMLDivElement>(null)

  const nodeVars = testCase.nodeVariables
  const hasNodeVars = !!nodeVars && Object.keys(nodeVars).length > 0
  const allConversationEntries = testCase.conversationHistory ?? []

  const scrollToBottom = () => {
    if (conversationScrollRef.current) {
      // ScrollArea forwards ref to Viewport element, which is the scrollable element
      const viewport = conversationScrollRef.current
      if (viewport && 'scrollTop' in viewport) {
        viewport.scrollTop = viewport.scrollHeight
      }
    }
  }

  useLayoutEffect(() => {
    if (isOpen) {
      // Small delay to ensure accordion content is rendered
      const timeoutId = setTimeout(() => {
        scrollToBottom()
      }, 200)
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [isOpen])

  const getNodeName = (nodeUuid: string | null | undefined): string | null => {
    if (!nodeUuid) return null
    const node = flowInstance?.getNode(nodeUuid)
    return node?.data?.config?.name || nodeUuid
  }

  // Helper to find node transition info from the next plivo entry after an intent
  const getNodeTransitionInfo = (currentIndex: number) => {
    const entry = allConversationEntries
      .slice(currentIndex + 1)
      .find(
        (entry) =>
          entry.role?.toLowerCase() === 'plivo' && (entry.from_node_uuid || entry.to_node_uuid),
      )

    if (!entry) {
      return { fromNode: null, toNode: null }
    }

    return {
      fromNode: entry.from_node_uuid
        ? getNodeName(entry.from_node_uuid) || entry.from_node_uuid
        : null,
      toNode: entry.to_node_uuid ? getNodeName(entry.to_node_uuid) || entry.to_node_uuid : null,
    }
  }

  const formatNodeVarValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return ''
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value)
    }

    return JSON.stringify(value, null, 2)
  }

  // Group node variables by node name
  const groupNodeVariables = (nodeVars: Record<string, unknown>) => {
    const parsed = Object.entries(nodeVars).map(([key, value]) => {
      const dotIndex = key.indexOf('.')
      const nodeName = dotIndex > 0 ? key.substring(0, dotIndex) : 'Unknown Node'
      const varName = dotIndex > 0 ? key.substring(dotIndex + 1) : key
      return { nodeName, varName, value }
    })

    const grouped = groupBy(parsed, 'nodeName')

    return Object.keys(grouped).reduce(
      (result, nodeName) => {
        result[nodeName] = grouped[nodeName].map(({ varName, value }) => ({ varName, value }))
        return result
      },
      {} as Record<string, Array<{ varName: string; value: unknown }>>,
    )
  }

  const roleDetailsMap: Record<
    string,
    { label: string; alignment: 'center' | 'start'; variant: 'system' | 'customer' | 'agent' }
  > = {
    system: {
      label: 'System',
      alignment: 'center',
      variant: 'system',
    },
    user: {
      label: 'Customer',
      alignment: 'start',
      variant: 'customer',
    },
    customer: {
      label: 'Customer',
      alignment: 'start',
      variant: 'customer',
    },
    assistant: {
      label: 'AI Agent',
      alignment: 'start',
      variant: 'agent',
    },
  }

  const getRoleDetails = (role: string) => {
    const normalized = role?.toLowerCase() ?? ''
    return (
      roleDetailsMap[normalized] ?? { label: '', alignment: 'start', variant: 'customer' as const }
    )
  }

  const renderBubble = (message: string, variant: 'agent' | 'customer' | 'system') => {
    if (message && message.length > 0) {
      return (
        <div
          className={cn(
            'rounded-xl px-3 py-2 text-p-400 whitespace-pre-wrap border-[0.5px] border-st2',
            (variant === 'customer' || variant === 'agent') && 'bg-bg2',
            variant === 'system' && 'bg-bubble-agent',
          )}
        >
          {message}
        </div>
      )
    }
    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[80vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-4 flex flex-row items-center">
          <DialogTitle className="text-h4-500">{testCase.name}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Accordion type="multiple" defaultValue={['conversation']}>
            {(allConversationEntries.length > 0 ||
              (testCase.userMessage && testCase.userMessage.length > 0)) && (
              <AccordionItem value="conversation" className="border-b-0">
                <AccordionTrigger
                  className="px-4 py-3 text-p-600 text-left justify-start gap-2"
                  iconPosition="left"
                >
                  Conversation
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <ScrollArea ref={conversationScrollRef} className="h-[52vh]">
                    <div className="mt-3 space-y-4 pr-2">
                      {allConversationEntries.length > 0
                        ? allConversationEntries
                            .map((msg: EvalTestCase['conversationHistory'][0], idx: number) => {
                              const role = msg.role?.toLowerCase() ?? ''
                              // Skip plivo entries for display
                              if (role === 'plivo') {
                                return null
                              }

                              const roleDetails = getRoleDetails(msg.role)
                              const hasIntent = !!msg.intent_chosen
                              const hasVariables =
                                !!msg.variables_extracted &&
                                Object.keys(msg.variables_extracted).length > 0
                              const hasToolCall = !!msg.tool_name

                              // Look ahead for plivo entry with node transition info
                              const nodeTransition = hasIntent ? getNodeTransitionInfo(idx) : null

                              const nodeName =
                                role === 'assistant' && msg.currentNodeUuid
                                  ? getNodeName(msg.currentNodeUuid)
                                  : null

                              return (
                                <div key={`${msg.role}-${idx}`} className="space-y-3">
                                  {msg.message && msg.message.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-xs-600">
                                        {roleDetails.label}
                                        {nodeName && (
                                          <span className="text-tertiary"> ({nodeName})</span>
                                        )}
                                      </span>
                                      <div className="flex w-full justify-start">
                                        {renderBubble(msg.message, roleDetails.variant)}
                                      </div>
                                    </div>
                                  )}

                                  {hasIntent && msg.intent_chosen && (
                                    <IntentDetectedCard
                                      intentName={msg.intent_chosen}
                                      previousNode={
                                        msg.previous_node ||
                                        nodeTransition?.fromNode ||
                                        getNodeName(msg.from_node_uuid) ||
                                        msg.from_node_uuid ||
                                        null
                                      }
                                      newNode={
                                        msg.new_node ||
                                        nodeTransition?.toNode ||
                                        getNodeName(msg.to_node_uuid) ||
                                        msg.to_node_uuid ||
                                        null
                                      }
                                    />
                                  )}

                                  {hasVariables && msg.variables_extracted && (
                                    <VariablesExtractedCard variables={msg.variables_extracted} />
                                  )}

                                  {hasToolCall && msg.tool_name && (
                                    <ToolCallIndicator toolName={msg.tool_name} />
                                  )}
                                </div>
                              )
                            })
                            .filter((entry) => entry !== null)
                        : null}

                      {testCase.userMessage && testCase.userMessage.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs-600">Customer</span>
                            <div className="flex w-full justify-start">
                              {renderBubble(testCase.userMessage, 'customer')}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            )}

            {hasNodeVars && (
              <AccordionItem value="nodevars" className="border-b-0">
                <AccordionTrigger
                  className="px-4 pb-3 text-p-600 text-left justify-start gap-2"
                  iconPosition="left"
                >
                  Simulated Node Variables
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <ScrollArea className="h-[34vh]">
                    <div className="mt-3 space-y-3 pr-2">
                      {Object.entries(groupNodeVariables(nodeVars ?? {})).map(
                        ([nodeName, variables]) => (
                          <div
                            key={nodeName}
                            className="rounded-xl border border-st2 bg-bubble-agent px-4 py-3 w-fit"
                          >
                            <div className="mb-2 text-xs-600">
                              <span className="text-tertiary">{nodeName}</span>
                            </div>
                            <div className="space-y-2">
                              {variables.map(({ varName, value }) => (
                                <div key={varName} className="grid grid-cols-12 gap-4 text-xs-400">
                                  <span className="col-span-7">{varName}</span>
                                  <span className="text-left  col-span-5">
                                    : {formatNodeVarValue(value)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </DialogContent>
    </Dialog>
  )
}
